import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

import { integrationsApi, useInitTodoIntegration } from '../../../data-access';
import './TodoistAuthCallback.scss';
import { Path } from '../../../routing/path';
import { getTodoOAuthExchangeRedirectUrl, trackEventMixpanel } from '../../../utils';

const TodoistAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { initTodoIntegration } = useInitTodoIntegration();
  const navigate = useNavigate();
  const hasExchanged = useRef(false);

  useEffect(() => {
    const exchange = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        navigate(Path.DASHBOARD);
        return;
      }

      // Prevent multiple calls
      if (hasExchanged.current) return;
      hasExchanged.current = true;

      const params = new URLSearchParams(new URL(state).search);
      const redirectTo = params.get('redirectTo');
      const userId = params.get('userId');
      const isMobileApp = params.get('isMobileApp')?.includes('true');
      const redirectDestination = redirectTo || Path.DASHBOARD;

      try {
        if (!userId) {
          navigate(redirectDestination);
          return;
        }

        const { data } = await integrationsApi.exchangeCode(
          code,
          'todoist',
          getTodoOAuthExchangeRedirectUrl(Path.CALLBACK.TODOIST_CALLBACK, searchParams)
        );

        if (!data) {
          navigate(redirectDestination);
          return;
        }

        const token = (data as { accessToken: string }).accessToken;
        const integrationId = await initTodoIntegration({
          token,
          todoType: 'todoist',
          userId,
          shouldSyncTasks: true,
          skipRefresh: isMobileApp,
        });

        trackEventMixpanel('connect_todo_auth_done', {
          todoType: 'todoist',
          fromMobileApp: isMobileApp,
        });

        // Redirect to mobile app if the request is from a mobile app
        if (isMobileApp) {
          window.location.href = 'lifestack://todo-callback';
        } else {
          navigate(redirectDestination);
        }
      } catch (error) {
        navigate(redirectDestination);
      }
    };

    exchange();
  }, [searchParams, navigate]);

  return (
    <div className="todoist-auth-callback">
      <CircularProgress />
    </div>
  );
};

export default TodoistAuthCallback;
